import { PAGE_SX } from '../components/pageLayout';
import { useEffect, useState } from "react";
import { Box, InputAdornment, TextField, Typography, useTheme } from "@mui/material";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import { format } from 'date-fns';

import { useAuthContext } from "../context/auth";
import { usePathContext } from "../context/path";
import { useConnectLogContext } from "../context/connectLog";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import Header from "../components/Header";
import PageBanner from '../components/PageBanner';
import { ROLES } from "../config/roles";
import DataGridCustomToolbar from "../components/DataGridCustomToolbar";
import CustomColumnMenu from "../components/DataGridCustomColumnMenu";
import CustomPagination from "../components/CustomPagination";
import Delete from "../components/connectLogs/Delete";
import { Search } from "@mui/icons-material";
import FlexBetween from "../components/FlexBetween";
import UserAvatar from '../components/chat/UserAvatar'
import { useUserContext } from "../context/user";
import useGridStyles from "../hooks/useGridStyles";

const ConnectLog = () => {
  // theme
  const theme = useTheme();
  const { gridSx, gridBorder } = useGridStyles();

  const { auth } = useAuthContext()
  const { setTitle } = usePathContext()
  const { connectLogs, dispatch } = useConnectLogContext()

  const axiosPrivate = useAxiosPrivate()
  const [ notFound, setNotFound ] = useState(false)
  const [ query, setQuery ] = useState("")
  const isRoot = auth.roles.includes(ROLES.Root)
  const admin = auth && isRoot

  const { users } = useUserContext();

  const [rows, setRows] = useState([]);
  const [totalPages, setTotalPages] = useState(0);

  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState({});
  const [search, setSearch] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const apiRef = useGridApiRef();

  // search
  const [searchInput, setSearchInput] = useState("");

  // data columns
  const columns = [
    {
      field: "order",
      headerName: "No.",
      flex: 0.2,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
          if (!params.api || !params.id) return null;
          const index = params.api.getAllRowIds?.().indexOf(params.id);
          const rowNumber = page * pageSize + (index + 1);
          return <span>{rowNumber}</span>;
      }
    },
    {
      field: "name",
      headerName: "Name",
      flex: 0.5,
      minWidth: 180,
      renderCell: (params) => {
        const log = params.row;

        // Match the connection log to the current user.
        const currentUser = users?.find(
          (user) =>
            (log.userId && user._id === log.userId) ||
            (log.email && user.email === log.email)
        );

        const selectedUser = {
          ...log,
          isOnline: currentUser?.isOnline ?? false,
          avatar: currentUser?.avatar ?? log.avatar,
        };

        return (
          <Box display="flex" alignItems="center" minWidth={0} gap={1}>
            <UserAvatar selectedUser={selectedUser} />

            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {log.name}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "email",
      headerName: "Email",
      flex: 0.7,
    },
    {
      field: "command",
      headerName: "Command",
      flex: 0.5,
    },
    {
      field: "regiment",
      headerName: "Regiment",
      flex: 0.5,
    },
    {
      field: "company",
      headerName: "Company",
      flex: 0.5,
    },
    {
      field: "outpost",
      headerName: "Outpost",
      flex: 0.5,
    },
    {
      field: "roles",
      headerName: "Roles",
      flex: 0.2,
    },
    {
      field: "deviceId",
      headerName: "Device ID",
      flex: 0.6,
    },
    {
      field: "ip",
      headerName: "IP",
      flex: 0.6
    },
    {
      field: "appVersion",
      headerName: "AppVersion",
      flex: 0.3
    },
    {
      field: "success",
      headerName: "Status",
      renderCell: (params) => {
        const isSuccess = params.value === true;
        const successColor = theme.palette.green?.[500] || "#4caf50"; // yellowish for success
        const failColor = "#f44336"; // Material red color or you can add to your tokens

        return (
          <span
            style={{
              display: "inline-block",          // allow block-level styling
              color: isSuccess ? successColor : failColor,
              fontWeight: "bold",
              whiteSpace: isSuccess ? "nowrap" : "normal", // 1 line for SUCCESS, wrap for FAIL
              lineHeight: "1.2em",
              maxHeight: isSuccess ? "1.2em" : "4.4em",
              verticalAlign: "middle",          // vertically center
              overflow: "hidden",
            }}
          >
            {isSuccess ? "SUCCESS" : params.row.message}
          </span>
        );
      },
      disableColumnMenu: true,       // optional: prevents menu in header
      renderHeader: (params) => <strong>{params.colDef.headerName}</strong>,
    }, 
    {
      field: "updatedAt",
      headerName: "Connect Time",
      flex: 0.7,
      renderCell: (params) => {
        return format(new Date(params.value), 'yyyy-MM-dd HH:mm:ss');
      }
    },    
    {
      field: "actions",
      // headerName: "Action",
      type: "actions",
      width: 40,
      renderCell: (params) => (
        <div>
          <Delete
            id={params.row._id}
            name={params.row.name}
            onDeleted={() => {
              setRefresh((prev) => prev + 1);
            }}
          />
        </div>                
      ),
  }
  ];

  useEffect(() => {
    setTitle("ConnectLog Management");

    const abortController = new AbortController();

    const getAllConnectLogs = async () => {
      setLoading(true);
      try {
        const response = await axiosPrivate.get('/api/connectLogs', {
          signal: abortController.signal,
          params: {
            page: page + 1,
            pageSize,
            search: debouncedQuery,
          },
        });

        setTotalCount(response.data.totalCount);
        setRows(response.data.items);
        setTotalPages(response.data.totalPages);
        setNotFound(false);
      } catch (err) {
        if (err.name !== 'CanceledError') {
          setNotFound(true);
        }
      } finally {
        if (!abortController.signal.aborted) setLoading(false);
      }
    };

    if (auth) {
      getAllConnectLogs();
    }

    return () => {
      abortController.abort();
    };
  }, [
    auth,
    page,
    pageSize,
    refresh,
    debouncedQuery,
    axiosPrivate,
    setTitle
  ]);

  // Wait until the user stops typing, then search (and start again from the first page)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query.trim());
      setPage(0);
    }, 400);

    return () => clearTimeout(timer);
  }, [query]);
 
  return (
    <>
      {admin && (
        <>
          <Box sx={PAGE_SX}>
            <FlexBetween
              sx={{
                flexDirection: {
                  xs: 'column',
                  sm: 'row',
                },
                alignItems: {
                  xs: 'flex-start',
                  sm: 'center',
                },
                gap: '1rem',
              }}
            >
              {/* Header */}
              <Header title="CONNECTLOGS" subtitle="Managing connection logs" />
              <PageBanner />

              {/* Search */}
              <TextField
                  label="Search"
                  placeholder="Name, email, unit, IP, message..."
                  variant="standard"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  sx={{
                      mb: "0.5rem",
                      mr: "1rem",
                      width: "15rem",
                  }}
                  InputProps={{
                      endAdornment: (
                      <InputAdornment position="end">
                          <Search sx={{ color: theme.palette.secondary[200] }} />
                      </InputAdornment>
                      ),
                  }}
              />
            </FlexBetween>

            {/* Content */}
            <Box
              mt="1rem"
              flex={1}
              minHeight="400px"
              backgroundColor={theme.palette.background.alt}
              border={gridBorder}
              borderRadius="0.55rem"
              overflow="hidden"
            >
              {/* Grid Table */}
              <DataGrid
                rowHeight={60}
                loading={loading}
                getRowId={(row) => row._id}
                rows={rows}
                columns={columns}
                rowCount={totalCount}
                pageSizeOptions={[20, 50, 100]}
                pagination
                paginationMode="server"
                paginationModel={{ page, pageSize }}
                onPaginationModelChange={(newModel) => {
                  setPage(newModel.page);
                  setPageSize(newModel.pageSize);
                }}
                onSortModelChange={(newSortModel) => setSort(newSortModel[0] || {})}
                components={{
                  Toolbar: DataGridCustomToolbar,
                  ColumnMenu: CustomColumnMenu,
                  Pagination: CustomPagination,
                }}
                componentsProps={{
                  pagination: {
                    apiRef,
                    count: totalCount,
                    page,
                    pageSize,
                    onPageChange: setPage,
                    onPageSizeChange: setPageSize,
                  },
                  toolbar: { searchInput, setSearchInput, setSearch },
                }}
                sx={gridSx}
              />
            </Box>
          </Box>   
        </> 
      )}

      {!loading && !rows.length && debouncedQuery && <div>No matching results found...</div>}

      {notFound && !debouncedQuery && !totalPages && (<div>No Record Found...</div>)}    </>
  )
}

export default ConnectLog